---
title: The Board
lede: Every task on one board, started on its own as session slots free.
order: 30
part: Plan the work
---

The Board holds every task in four lanes and starts the ones you queue as soon as a session slot is free. Open it with the **Board** icon at the foot of the navigator. There is one board for the whole app, because the session limits count every workspace together; each card names its workspace.

![The Board](media/board-columns.webp)

### Lanes

| Lane | What it holds |
|---|---|
| **Backlog** | Tasks for later |
| **To do** | Ready: the top task starts as soon as a slot frees |
| **In development** | A session is working on it |
| **Waiting** | The agent handed back, or the work is merged. Review the session, then close it |

You move tasks between **Backlog** and **To do**; the board moves them on from there. A lane that refuses a drop greys out and says why. When nothing more can start, **In development** shows `3/3 BUSY` against the limit in [Defaults and limits](/limits).

### Board or list

The header switch shows **Board** (lanes side by side) or **List** (one row per task, grouped by lane). A row shows the title, the workspace, where its run is, such as `2/3 Feature · Review · waiting on you`, and when it last moved.

![The List view](media/board-list.webp)

### Add a task

Press **Create** and pick **Task** (`⌘N`), fill in [the task form](/task-form), then **Add**. The task waits in **Backlog** until you move it to **To do**. Opened from **Backlog**, its form also offers **Run**: it saves the task and moves it to **To do**, where it starts as soon as there is room. A task marked **Urgent** starts ahead of the queue, over both limits.

The same menu offers **Epic**, to write an [epic](/epics) of subtasks by hand, and **Epic with Agent**, which opens the Agent panel (`⌘J`) to explore a workspace and propose the work, as one task or an epic.

### Cards

A card shows its workspace, title, the start of the prompt, and its workflow, model, effort and branch. A card from a ticket carries the ticket's key. A card the board could not start says why. **Continue** releases a [workflow](/workflows) parked at a gate; **Review** opens the agent's proposed workflow; **Open** opens a session whose run waits on you for another reason. **Open session** goes to the card's session, on its agent's tab.

![A task card](media/board-card.webp)

Right-click a card, or press `⇧F10`, for its menu: **Open**, **Open session**, **Move to To do** or **Move to Backlog**, **Archive** (a finished epic) and **Delete**. **Delete** works from **Backlog** only; elsewhere it says what to do first. Deleting a task leaves its ticket alone.

### Filter by workspace

With two or more workspaces, chips above the lanes show **All** and each workspace. A chip narrows what you see, never what starts.

### Tickets

With [Jira or Trello](/integrations), tickets assigned to you arrive in **Backlog**, or **To do** when their status maps there. **Sync tickets** imports now. Mapped lanes move both ways: moving a card moves its ticket and the reverse; when both moved, the later move wins.

### Keys

| Key | **Board** | **List** |
|---|---|---|
| `↩` | Open the card | Open the task |
| `⇧F10` | Its menu | Its menu |
| `⇧←` / `⇧→` | Move to the lane before or after | |
| `↑` `↓` `Home` `End` | | Walk the rows |
| `⌥↑` / `⌥↓` | | Move the task up or down, across lanes where allowed |

### Closing a task

Closing a task's session files it in [the Archive](/archive), or puts it back in **Backlog** with **Send the task back to the backlog**.
